import { injectable } from 'inversify'
import QrCodeWorkerUrl from '@/workers/qrcode.worker.ts?worker&url'
import { createWorkerCrossoriginWorkaround } from '@/workers/utils/createWorkerCrossoriginWorkaround'
import { TmQrCodeWorkerError } from '@/core/error/worker/TmQrCodeWorkerError'
import {
  isWorkerQrCodeToDataUrlPayloadSuccessPayload,
  isWorkerQrCodeToDataUrlPayloadErrorPayload,
  W_QRCODE_TO_DATA_URL_MSG,
  type QrCodeToDataURLOptions,
  type WorkerQrCodeToDataUrlPayload,
} from '@/services/workers/qrcode/types'

@injectable()
export class QrCodeWorkerService {
  private worker: Worker

  public async toDataUrl(text: string, options: QrCodeToDataURLOptions): Promise<string> {
    const nonce = Math.random()

    return new Promise((resolve, reject) => {
      // Acquire the worker instance
      const worker = this.getWorker()

      const eventHandler = (event: MessageEvent<unknown>) => {
        if (isWorkerQrCodeToDataUrlPayloadSuccessPayload(event.data) && event.data.nonce === nonce) {
          resolve(event.data.dataUrl)
        } else if (isWorkerQrCodeToDataUrlPayloadErrorPayload(event.data) && event.data.nonce === nonce) {
          reject(new TmQrCodeWorkerError(event.data.error))
        }

        worker.removeEventListener('message', eventHandler)
      }

      // Subscribe to events
      worker.addEventListener('message', eventHandler)

      // Send a message to the worker
      worker.postMessage({
        type: W_QRCODE_TO_DATA_URL_MSG,
        nonce,
        text,
        options,
      } satisfies WorkerQrCodeToDataUrlPayload)
    })
  }

  private getWorker(): Worker {
    if (!this.worker) {
      this.worker = createWorkerCrossoriginWorkaround(QrCodeWorkerUrl)

      this.worker.addEventListener('messageerror', () => {
        throw new TmQrCodeWorkerError('Worker received a message that could not be deserialized')
      })

      this.worker.addEventListener('error', (error) => {
        throw new TmQrCodeWorkerError(error.message)
      })
    }

    return this.worker
  }
}
